import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, Typography } from 'antd';
import { Spinner } from '../../../Spinner';
import { colors, motion, radii, spacing } from '../../../../theme-v2';
import type { StandoutItem, StandoutsAnalyticEvents } from './types';
import { AddonLayout } from '../AddonLayout';
import { standoutHasSource } from './utils';
import { StandoutNotationSwatch } from './StandoutNotationSwatch';

const { Text, Title } = Typography;

/** Handwritten panel title, per the design. Caveat is loaded by the app. */
const titleStyle: CSSProperties = {
  fontFamily: "'Caveat', cursive",
  fontSize: 30,
  fontWeight: 600,
  lineHeight: 1,
  margin: 0,
};

const stateStyle: CSSProperties = { minHeight: 160 };

export interface StandoutsProps {
  /** Passages to list (parsed from the `Standouts` add-on asset). */
  standouts: StandoutItem[];
  /** Shows a spinner instead of the list while the asset loads. */
  loading?: boolean;
  /** Shows an error state instead of the list. */
  error?: boolean;
  /** Clicking a row — the app marks that standout's zone in the document. */
  onStandoutClick?: (standout: StandoutItem) => void;
  className?: string;
  /** Interaction callbacks (item click). */
  analyticEvents?: StandoutsAnalyticEvents;
}

/**
 * Standouts — the Knowledge Hub add-on listing the passages worth remembering.
 * Clicking a row marks that passage in the document; the row's icon and color
 * match the mark left there. Stateless: the app owns which
 * rows are marked and what a click does.
 */
export const Standouts = ({
  standouts,
  loading,
  error,
  onStandoutClick,
  className,
  analyticEvents,
}: StandoutsProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  const stateMessage = (message: string) => (
    <AddonLayout
      className={className}
      align="center"
      justify="center"
      style={{ ...stateStyle, padding: spacing.lg }}
    >
      {message}
    </AddonLayout>
  );

  if (loading)
    return (
      <AddonLayout
        className={className}
        align="center"
        justify="center"
        style={stateStyle}
      >
        <Spinner />
      </AddonLayout>
    );
  if (error) return stateMessage(t('experienceV2.standouts.error'));
  // A standout without sources can't open anything, so it stays out of the
  // list until generation fills them.
  const visible = standouts.filter(standoutHasSource);
  if (visible.length === 0)
    return stateMessage(t('experienceV2.standouts.empty'));

  return (
    <AddonLayout
      className={className}
      gap={spacing.base}
      style={{ padding: spacing.lg }}
    >
      <Flex vertical gap={spacing.xs}>
        <Title level={4} style={titleStyle}>
          {t('experienceV2.standouts.title')}
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('experienceV2.standouts.subtitle')}
        </Text>
      </Flex>
      <Flex vertical gap={spacing.sm}>
        {visible.map((standout, index) => (
          <StyledRow
            key={standout.id}
            type="button"
            onClick={() => {
              analyticEvents?.onItemClick?.(index, visible.length);
              onStandoutClick?.(standout);
            }}
          >
            <StandoutNotationSwatch
              notation={standout.marker.notation}
              color={standout.marker.color}
            />
            <Text style={{ fontSize: 13 }}>{standout.title}</Text>
          </StyledRow>
        ))}
      </Flex>
    </AddonLayout>
  );
};

// ─── Styled components ───
const StyledRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.sm}px;
  width: 100%;
  padding: ${spacing.md}px 14px;
  text-align: left;
  background: ${colors.surface.base};
  border: 1px solid ${colors.border.subtle};
  border-radius: ${radii.box}px;
  cursor: pointer;
  transition:
    border-color ${motion.duration.fast} ${motion.easing.standard},
    background ${motion.duration.fast} ${motion.easing.standard};

  &:hover {
    border-color: ${colors.accent.border};
    background: ${colors.accent.tint};
  }
`;
